/**
 * Development-only network delay utility.
 * Simulates real-world network latency to help test loading states during development.
 * 
 * Only applies in development environment (NODE_ENV === 'development')
 */

import { config } from "../config";

const DEV_DELAY_MIN = 250; // ms
const DEV_DELAY_MAX = 1200; // ms

/**
 * Generate a random delay between min and max milliseconds
 */
const getRandomDelay = () => {
  return Math.floor(Math.random() * (DEV_DELAY_MAX - DEV_DELAY_MIN + 1)) + DEV_DELAY_MIN;
};

/**
 * Returns true if we should apply dev delays
 */
export const shouldApplyDevDelay = () => {
  return config.ENVIRONMENT === 'development';
};

/**
 * Sleep for a random duration if in development mode
 * @param {string} [label] - Optional label to include in the log message
 * @returns {Promise<void>}
 */
export const devDelay = async (label) => {
  if (!shouldApplyDevDelay()) return;
  
  const delay = getRandomDelay();
  const suffix = label ? ` for ${label}` : '';
  await new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Wrap an async function to add dev delay before resolving
 * @param {Function} asyncFn - The async function to wrap
 * @returns {Function} - Wrapped function with dev delay
 */
export const withDevDelay = (asyncFn) => {
  return async (...args) => {
    await devDelay();
    return asyncFn(...args);
  };
};

export default devDelay;

